import React from 'react';
import { AlertTriangle, Check, Crosshair, Eye, ShieldX } from 'lucide-react';
import { Chip, Pill } from '../shared';
import type { Assignment, Attention, CurrentFocus, Lifecycle, Load } from '../shared';
import { LIFECYCLE_META, LOAD_STATE_META } from '../shared';

// ── Employee & capabilities ─────────────────────────────────────────────────

export const SkillChips: React.FC<{ skills: string[] }> = ({ skills }) => {
  if (skills.length === 0) return null;
  return (
    <div className="mt-1.5 flex max-w-[190px] flex-wrap gap-1">
      {skills.map((skill, i) => (
        <Chip key={`${skill}-${i}`}>{skill}</Chip>
      ))}
    </div>
  );
};

// ── Lifecycle badge ─────────────────────────────────────────────────────────

export const LifecycleBadge: React.FC<{ lifecycle: Lifecycle }> = ({ lifecycle }) => {
  const meta = LIFECYCLE_META[lifecycle];
  return (
    <Pill tone={meta.pill} dot>
      {meta.label}
    </Pill>
  );
};

// ── Load (assignment count + capacity) ─────────────────────────────────────

export const LoadCell: React.FC<{ load: Load; lifecycle: Lifecycle }> = ({ load, lifecycle }) => {
  if (lifecycle === 'terminated') {
    return (
      <div className="flex min-w-[96px] flex-col gap-1.5">
        <div className="flex items-center gap-1.5 font-mono text-[12.5px]">
          <span className="text-faint">— / —</span>
        </div>
      </div>
    );
  }

  const { activeAssignments, state, capacity, headroom } = load;
  const meta = LOAD_STATE_META[state];
  const frozen = state === 'frozen';

  // Build the count display and context sub-line.
  let countDisplay: React.ReactNode;
  let subLine: string;

  if (capacity != null) {
    countDisplay = (
      <span className="font-semibold text-heading">
        {activeAssignments}
        <span className="font-normal text-muted">/{capacity}</span>
      </span>
    );
    if (state === 'over_capacity') {
      const overBy = headroom != null ? Math.abs(headroom) : activeAssignments - capacity;
      subLine = `over by ${overBy}`;
    } else if (state === 'at_capacity') {
      subLine = 'at capacity';
    } else if (state === 'assigned') {
      const slots = headroom ?? capacity - activeAssignments;
      subLine = `${slots} slot${slots === 1 ? '' : 's'} free`;
    } else {
      // unassigned or frozen with capacity
      subLine = `${capacity} slot${capacity === 1 ? '' : 's'} free`;
    }
  } else {
    // Unbounded: show raw count and fallback sub-line.
    countDisplay = <span className="font-semibold text-heading">{activeAssignments}</span>;
    subLine = activeAssignments === 1 ? '1 active assignment' : `${activeAssignments} active assignments`;
  }

  return (
    <div className="flex min-w-[96px] flex-col gap-1.5">
      <div className="flex items-center gap-1.5 font-mono text-[12.5px]">
        {countDisplay}
        <Pill tone={meta.pill} size="xs">
          {meta.label}
        </Pill>
      </div>
      <div className={`font-mono text-[10px] text-faint ${frozen ? 'opacity-50' : ''}`}>{subLine}</div>
    </div>
  );
};

// ── Attention (aggregated, traced to a project) ─────────────────────────────

const ATTENTION_ICON = { check: Check, eye: Eye, alert: AlertTriangle, 'shield-x': ShieldX } as const;

export const AttentionCell: React.FC<{ attention: Attention }> = ({ attention }) => {
  if (attention.level === 'clear') {
    return (
      <div className="inline-flex flex-col gap-0.5">
        <span className="inline-flex items-center gap-1.5 text-xs text-faint">
          <Check size={13} /> Clear
        </span>
      </div>
    );
  }
  const map = {
    needs_review: { label: 'Needs review', icon: 'eye' as const, text: 'text-warning-text' },
    blocked: { label: 'Blocked', icon: 'alert' as const, text: 'text-danger-text' },
    permission_needed: { label: 'Permission needed', icon: 'shield-x' as const, text: 'text-purple-text' },
  }[attention.level];
  const Icon = ATTENTION_ICON[map.icon];
  const sub = [attention.detail, attention.sourceProject].filter(Boolean).join(' · ');
  return (
    <div className="inline-flex flex-col gap-0.5">
      <span className={`inline-flex items-center gap-1.5 text-xs ${map.text}`}>
        <Icon size={13} /> {map.label}
      </span>
      {sub && <span className={`ml-[19px] font-mono text-[10px] ${map.text}`}>{sub}</span>}
    </div>
  );
};

// ── Assignments axis ────────────────────────────────────────────────────────

export const AssignmentsCell: React.FC<{ assignments: Assignment[]; terminated?: boolean }> = ({
  assignments,
  terminated = false,
}) => {
  const active = assignments.filter((a) => a.active);
  if (active.length === 0) {
    return (
      <span className={`text-xs ${terminated ? 'text-faint' : 'text-muted'}`}>
        {terminated ? 'None · unassigned on exit' : 'None'}
      </span>
    );
  }
  const shown = active.slice(0, 2);
  const more = active.length - shown.length;
  return (
    <div className="flex flex-col gap-1">
      {shown.map((a) => (
        <div key={a.projectId} className="flex items-center gap-1.5 text-xs text-body">
          <span className="h-[7px] w-[7px] shrink-0 rounded-xs bg-accent" aria-hidden="true" />
          {a.projectName}
        </div>
      ))}
      {more > 0 && <div className="ml-[13px] font-mono text-[10px] text-faint">+{more} more</div>}
    </div>
  );
};

// ── Current focus (single hint) ─────────────────────────────────────────────

export const CurrentFocusCell: React.FC<{ focus: CurrentFocus }> = ({ focus }) => {
  if (!focus.label) {
    return (
      <span className="inline-flex max-w-[180px] items-center gap-1.5 text-xs text-faint">
        <Crosshair size={13} className="shrink-0" />—
      </span>
    );
  }
  return (
    <span className="inline-flex max-w-[180px] items-center gap-1.5 text-xs text-body">
      <Crosshair size={13} className="shrink-0 text-accent-text" />
      <span className="truncate font-mono text-[11px]">{focus.label}</span>
    </span>
  );
};
